import { useLayoutEffect, useState } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'
import { routeFamilyOf, foreignKeysFor } from './routeStateContract'

/** §1 arrival prune (spec 2026-07-31): pin/neutral routes host only the keys their family
 *  owns. Continuous — keyed on the live search string, so deep links, bug-era bookmarks,
 *  Back-navigation into dirty entries, and post-mount foreign writes (advisor steering from
 *  Home) all get cleaned. ONE replace-write per dirty state; strict no-op on review
 *  (useReviewPageState's reconcile owns Review cleanup — single-writer doctrine) and on
 *  unfamilied routes (login/callback — never touch auth params). Mounted ONCE in AppLayout. */
export function useRouteStateHygiene(): void {
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  // Mount/update ordering hazard: MemoryRouter/BrowserRouter subscribe their history
  // listener in their OWN useLayoutEffect, which — as the ancestor — fires AFTER this
  // hook's layout effect on the tree's first commit. A write issued in that same first
  // effect pass lands on `history` but has no listener yet to propagate it into React
  // state, so it's silently dropped. Deferring the write to the render after mount
  // guarantees the router's listener is already subscribed.
  const [ready, setReady] = useState(false)
  useLayoutEffect(() => setReady(true), [])
  useLayoutEffect(() => {
    if (!ready) return
    const family = routeFamilyOf(location.pathname)
    if (family === null || family === 'review') return
    const foreign = foreignKeysFor(family, searchParams)
    if (foreign.length === 0) return
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev)
        for (const key of foreign) params.delete(key)
        return params
      },
      { replace: true },
    )
  }, [ready, location.pathname, searchParams, setSearchParams])
}
